import React, { createContext, useCallback, useMemo, useState } from "react";

import { v4 as uuid } from "uuid";

import { AutomaticSequencing } from "../../../models/automatic.sequencing";
import { CommodityCode } from "../../../models/commodity.code";
import { ShipmentHistory } from "../../../models/history";
import { Shipment } from "../../../models/shipments";
import { ShipmentDetails, ShipmentType } from "../../../util/enums";
import { ShipmentChangeKeys, ShipmentChangeOptions, ShipmentObjects } from "../../Booking/util";
import { ShipmentKey } from "../types";

interface ShipmentContextProps {
  shipment?: Shipment;
  setShipment: React.Dispatch<React.SetStateAction<Shipment>>;
  handleUpdateShipment: (attribute: ShipmentKey, value: any) => void;
  histories: ShipmentHistory[];
  setHistories: (value: ShipmentHistory[]) => void;
  rootContainerId: string;
  setRootContainerId: React.Dispatch<React.SetStateAction<string>>;
  hasUnsavedChanges: boolean;
  showContainerSection: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  changedOptions: ShipmentChangeKeys;
  handleChangeOptions: (newOption: ShipmentChangeOptions, noChanges?: boolean) => void;
  sequence: AutomaticSequencing;
  setSequence: React.Dispatch<React.SetStateAction<AutomaticSequencing>>;
  hsCodes: CommodityCode[];
}

const ShipmentContext = createContext<ShipmentContextProps>({} as ShipmentContextProps);

interface ShipmentProviderProps {
  children: React.ReactChild;
}

const ShipmentProvider = ({ children }: ShipmentProviderProps) => {
  const [shipment, setShipment] = useState<Shipment>({
    containers: [{ uuid: uuid() }],
  } as Shipment);
  const [rootContainerId, setRootContainerId] = useState<string>("");
  const [histories, setHistories] = useState<ShipmentHistory[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [sequence, setSequence] = useState<AutomaticSequencing>({} as AutomaticSequencing);

  const [changedOptions, setChangedOptions] = useState<ShipmentChangeKeys>({
    [ShipmentObjects.SHIPMENT]: false,
    [ShipmentObjects.CONTAINERS]: false,
    [ShipmentObjects.MILESTONES]: false,
    [ShipmentObjects.PURCHASE_ORDERS]: false,
    [ShipmentObjects.CARTONS]: false,
    [ShipmentObjects.DOCUMENTS]: false,
  });

  const showContainerSection = useMemo(
    () => shipment?.[ShipmentDetails.SHIPMENT_TYPE] !== ShipmentType.AIR,
    [shipment?.[ShipmentDetails.SHIPMENT_TYPE]]
  );

  const hsCodes = useMemo(() => {
    const uniqueCode: CommodityCode[] = [];
    const cartons = shipment?.cartons;
    const addHsCode = (hscode?: string) => {
      if (hscode) {
        if (!uniqueCode?.find((value) => value.hscode === hscode)) {
          uniqueCode.push({
            hscode,
            section: "",
            description: "",
            parent: "",
            level: 0,
          });
        }
      }
    };

    if (cartons && cartons?.length) {
      for (const carton of cartons) {
        const products = carton?.products;

        if (products && products?.length) {
          for (const product of products) {
            addHsCode(product?.hsCode);
          }
        }
      }
    }

    return uniqueCode;
  }, [shipment?.cartons]);

  const handleUpdateShipment = useCallback((attribute: ShipmentKey, value: any) => {
    if (attribute && attribute !== "all") {
      if (!["string", "number", "boolean", "object"].includes(typeof value))
        setShipment((state: any) => ({ ...state, [attribute]: [...value] }));
      else setShipment((state: any) => ({ ...state, [attribute]: value }));
    } else {
      setShipment((state: any) => ({ ...state, ...value }));
    }
  }, []);

  const handleChangeOptions = useCallback(
    (newOption: ShipmentChangeOptions, noChanges?: boolean) => {
      if (!noChanges) setHasUnsavedChanges(true);

      if (!changedOptions[newOption])
        setChangedOptions((prev) => {
          return {
            ...prev,
            [newOption]: true,
          };
        });
    },
    [changedOptions]
  );

  return (
    <ShipmentContext.Provider
      value={{
        shipment,
        setShipment,
        handleUpdateShipment,
        histories,
        setHistories,
        rootContainerId,
        setRootContainerId,
        handleChangeOptions,
        changedOptions,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        showContainerSection,
        sequence,
        setSequence,
        hsCodes,
      }}
    >
      {children}
    </ShipmentContext.Provider>
  );
};

export { ShipmentContext, ShipmentProvider };
